import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Filters,
  IconLoader,
  Table,
  useFilters,
  useQueryState,
  useScroll,
  usePrevious,
  useGetList,
  SchemaFilterType,
  formatDate,
  SelectItemProps,
  ButtonSecondary,
  ButtonSize,
  IconDownload,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  columns,
  columnsLabels,
  schema,
  filtersSchema,
} from './variables/list';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  useLazyGetListOfWarningsQuery,
  useLazyGetOrganizationsByIdsQuery,
  useExportListOfWarningReportMutation,
  useLazyGetMonitoringRulesByIdsQuery,
} from './store/service';
import { ListOfWarnings as ListOfWarningsTypes } from './types';
import { clearListOfWarningsState } from './store/slice';
import { RiskScoreRange } from './store/types';
import useGetAutocompleteList from './hooks/use-get-organizations';
import useGetMonitoringRulesAutocmpleteList from './hooks/use-get-monitoring-rules';
import WarningsReportFilters from '../../components/warnings-report/filters';

import cn from './warning-report.module.css';

let organizationSearchTimeout: NodeJS.Timeout;

const formatListOfWarnings = (
  list: ListOfWarningsTypes[],
  riskScoreRanges?: RiskScoreRange[],
): ListOfWarningsTypes[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    let riskLevel;
    let riskLevelClassName;

    if (result.scoreResult) {
      riskLevel = riskScoreRanges?.find(
        (range) =>
          (result.scoreResult?.totalRiskScore as number) >= range.rangeStart &&
          (result.scoreResult?.totalRiskScore as number) <= range.rangeEnd,
      );
    } else if (result.riskScore) {
      riskLevel = riskScoreRanges?.find(
        (range) =>
          (result.riskScore as number) >= range.rangeStart &&
          (result.riskScore as number) <= range.rangeEnd,
      );
    }

    if (riskLevel) {
      if (riskLevel?.action === 'NONE') {
        riskLevelClassName = cn.lowLever;
      } else if (riskLevel?.action === 'OPS_REVIEW') {
        riskLevelClassName = cn.mediumLevel;
      } else {
        riskLevelClassName = cn.highLevel;
      }
    }

    let warningType = '-';

    if (result.type) {
      warningType = result.type
        .split('_')
        .map((type) => `${type.charAt(0)}${type.slice(1).toLowerCase()}`)
        ?.join(' ');

      if (result.type === 'BLACKLIST') {
        warningType = 'Blocklist';
      }
    }

    const render: Record<string, React.JSX.Element> = {
      createdAt: <div>{formatDate(result.createdAt, true)}</div>,
      warningEntity: (
        <div>
          {result.warningEntity === 'ORGANIZATION'
            ? 'Customer'
            : result.warningEntity
                .split('_')
                .map(
                  (warningEntity) =>
                    `${warningEntity.charAt(0)}${warningEntity
                      .slice(1)
                      .toLowerCase()}`,
                )
                ?.join(' ')}
        </div>
      ),
      organizationId: (
        <div>
          {result?.organizationId
            ? result?.organization?.companyName ||
              `${result?.organization?.firstName} ${result?.organization?.lastName}`
            : '-'}
        </div>
      ),
      applicationId: (
        <div>
          {result?.applicationId
            ? result?.application?.companyName ||
              `${result?.application?.firstName} ${result?.application?.lastName}`
            : '-'}
        </div>
      ),
      actions: (
        <div>
          {result.actions
            ? result.actions
                .split('_')
                .map(
                  (action) =>
                    `${action.charAt(0)}${action.slice(1).toLowerCase()}`,
                )
                ?.join(' ')
            : '-'}
        </div>
      ),
      matchStatus: (
        <div>
          {result?.matchStatus
            ? `${result?.matchStatus?.charAt(0)}${result?.matchStatus?.slice(1).toLowerCase()}`
            : '-'}
        </div>
      ),
      employeeId: (
        <div>
          {result?.employee &&
          result?.employee?.profile?.firstName &&
          result?.employee?.profile?.lastName
            ? `${result?.employee?.profile?.firstName} ${result?.employee?.profile?.lastName}`
            : '-'}
        </div>
      ),
      blacklistId: (
        <div>{result?.blacklistId ? result?.blacklist?.name : '-'}</div>
      ),
      riskScoreFrom: (
        <div className={cn.riskScore}>
          {result?.scoreResult?.totalRiskScore || result?.riskScore ? (
            <>
              <div className={`${cn.riskScoreValue} ${riskLevelClassName}`}>
                {result?.scoreResult?.totalRiskScore || result?.riskScore}
              </div>
              <div className={cn.riskScoreLevel}>
                {riskLevel?.name.toLowerCase()}
              </div>
            </>
          ) : (
            '-'
          )}
        </div>
      ),
      monitoringRuleId: (
        <div>
          {result.monitoringRuleId ? result?.monitoringRule?.name : '-'}
        </div>
      ),
      type: <div>{warningType}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const ListOfWarnings = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [takeCount, setTakeCount] = useState(0);
  const [listFilters, setFilters] = useState<{
    organizationType?: string[];
    transactionTypeId?: string[];
    transactionPaymentType?: string[];
    paymentOrderType?: string[];
  }>({
    organizationType: undefined,
    transactionTypeId: undefined,
    transactionPaymentType: undefined,
    paymentOrderType: undefined,
  });

  const {
    warningsReport: {
      listOfWarnings,
      listOfWarningsPagination,
      riskScoreRanges,
      blacklists,
      transactionTypes,
      paymentTypes,
      errorsList,
    },
    reportsDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const [getList, { isFetching, isLoading }] = useLazyGetListOfWarningsQuery();
  const [getOrganizationsByIds] = useLazyGetOrganizationsByIdsQuery();
  const [getMonitoringRulesByIds] = useLazyGetMonitoringRulesByIdsQuery();
  const [
    exportListOfWarningReport,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportListOfWarningReportMutation();

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const { search } = useLocation();

  const { getListAction, setPage, onFilter, errorList } = useGetList<any>({
    getList,
    onFilterChange,
    onSortChange,
    schema,
  });

  const {
    autocompleteList,
    onSearch,
    organizationIsLoading,
    setSelectedList,
    selectedList,
    organizationSearchText,
    onFilterScroll,
    nextPageIsLoading: organizationsNextPageIsLoading,
    pagination: organizationsPagination,
  } = useGetAutocompleteList();

  const {
    autocompleteList: monitoringRulesList,
    monitoringRulesIsLoading,
    setSelectedList: setMonitoringRulesSelectedList,
    selectedList: monitoringRulesSelectedList,
    onScroll,
    nextPageIsLoading,
    pagination,
  } = useGetMonitoringRulesAutocmpleteList();

  const dispatch = useAppDispatch();

  const onExportClick = async () => {
    const exportFilters: Record<string, string[] | string> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'cursor') {
          if (item !== 'createdAt') {
            if (
              item === 'organizationId' ||
              item === 'type' ||
              item === 'warningEntity' ||
              item === 'blacklistId' ||
              item === 'organizationType' ||
              item === 'transactionTypeId' ||
              item === 'transactionPaymentType' ||
              item === 'paymentOrderType'
            ) {
              exportFilters[item] = filters[item].value;
            } else {
              const [filterValue] = filters[item].value;

              exportFilters[item] = filterValue;
            }
          } else if (item === 'createdAt') {
            const createdAt = filters[item];

            const [from, to] = createdAt.value;

            exportFilters.createdAtFrom = from;

            if (to) {
              exportFilters.createdAtTo = to;
            }
          }
        }
      });
    }

    await exportListOfWarningReport(exportFilters);
  };

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'organizationId') {
      clearTimeout(organizationSearchTimeout);

      organizationSearchTimeout = setTimeout(() => {
        onSearch(searchAutocomplete);
      }, 500);
    }
  };

  const getOrganizationsByIdsAction = useCallback(
    async (ids: string[], selectedListValues: SelectItemProps[]) => {
      const idsQuery = `&ids=${ids?.join(`&ids=`)}`;

      const response = await getOrganizationsByIds(idsQuery);

      if (response?.data?.data) {
        const nextList = ([] as SelectItemProps[]).concat(selectedListValues);

        response?.data?.data?.edges?.forEach((item) =>
          nextList.push({
            name:
              item.node?.type === 'CORPORATE'
                ? (item.node.companyName as string)
                : `${item.node.firstName} ${item.node.lastName}`,
            value: item.node.id,
          }),
        );

        setSelectedList(nextList);
      }
    },
    [],
  );

  const getMonitoringRulesByIdsAction = useCallback(
    async (ids: string[], selectedListValues: SelectItemProps[]) => {
      const idsQuery = `&id=${ids?.join(`&id=`)}`;

      const response = await getMonitoringRulesByIds(idsQuery);

      if (response?.data?.data) {
        const nextList = ([] as SelectItemProps[]).concat(selectedListValues);

        response?.data?.data?.edges?.forEach((item) =>
          nextList.push({
            name: item.node.name,
            value: item.node.id,
          }),
        );

        setMonitoringRulesSelectedList(nextList);
      }
    },
    [],
  );

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
    loaders: {
      organizationId: organizationIsLoading,
      monitoringRuleId: Boolean(monitoringRulesIsLoading && !nextPageIsLoading),
    },
    selectFunctions: {
      organizationId: {
        selectedList,
        setSelectedList,
      },
      monitoringRuleId: {
        selectedList: monitoringRulesSelectedList,
        setSelectedList: setMonitoringRulesSelectedList,
      },
    },
    searchTexts: {
      organizationId: organizationSearchText,
    },
    onScrollFunctions: {
      monitoringRuleId: {
        onScroll,
        nextPageIsLoading,
        hasNextPage: Boolean(pagination?.hasNextPage),
      },
      organizationId: {
        onScroll: onFilterScroll,
        nextPageIsLoading: organizationsNextPageIsLoading,
        hasNextPage: Boolean(organizationsPagination?.hasNextPage),
      },
    },
  });

  const onFilterAction = (filtersValues: {
    organizationType?: string[];
    transactionTypeId?: string[];
    transactionPaymentType?: string[];
    paymentOrderType?: string[];
  }) => {
    const nextFiltersValues: {
      organizationType?: string[];
      transactionTypeId?: string[];
      transactionPaymentType?: string[];
      paymentOrderType?: string[];
      createdAtFrom?: string;
      createdAtTo?: string;
    } = { ...filtersValues };

    setFilters(nextFiltersValues);

    const nextFilters: Filters = {
      ...filters,
      take: {
        value: [takeCount?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (
      Object.keys(nextFiltersValues)?.filter(
        // @ts-ignore
        (item) => nextFiltersValues[item],
      )?.length
    ) {
      if (nextFiltersValues.paymentOrderType) {
        nextFilters.paymentOrderType = {
          value: nextFiltersValues.paymentOrderType,
          type: SchemaFilterType.STRING,
        };
      }

      if (nextFiltersValues.transactionPaymentType) {
        nextFilters.transactionPaymentType = {
          value: nextFiltersValues.transactionPaymentType,
          type: SchemaFilterType.STRING,
        };
      }

      if (nextFiltersValues.organizationType) {
        nextFilters.organizationType = {
          value: nextFiltersValues.organizationType,
          type: SchemaFilterType.STRING,
        };
      }

      if (nextFiltersValues.transactionTypeId) {
        nextFilters.transactionTypeId = {
          value: nextFiltersValues.transactionTypeId,
          type: SchemaFilterType.STRING,
        };
      }
    }

    if (!nextFiltersValues.paymentOrderType && nextFilters.paymentOrderType) {
      delete nextFilters.paymentOrderType;
    }

    if (
      !nextFiltersValues.transactionPaymentType &&
      nextFilters.transactionPaymentType
    ) {
      delete nextFilters.transactionPaymentType;
    }

    if (!nextFiltersValues.organizationType && nextFilters.organizationType) {
      delete nextFilters.organizationType;
    }

    if (!nextFiltersValues.transactionTypeId && nextFilters.transactionTypeId) {
      delete nextFilters.transactionTypeId;
    }

    onFilter(nextFilters);
  };

  const formattedData = useMemo(() => {
    const data = formatListOfWarnings(
      listOfWarnings as ListOfWarningsTypes[],
      riskScoreRanges,
    );

    return data;
  }, [listOfWarnings, riskScoreRanges]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 46;

        setTakeCount(Math.ceil(take + 3));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (autocompleteList) {
      const nextList: SelectItemProps[] = [];

      selectedList?.forEach((item) => nextList.push(item));

      autocompleteList?.forEach((item) => {
        if (!nextList?.find((listItem) => listItem?.value === item.value)) {
          nextList.push(item);
        }
      });

      setValues('organizationId', nextList);
    }
  }, [autocompleteList, selectedList]);

  useEffect(() => {
    if (monitoringRulesList) {
      const nextList: SelectItemProps[] = [];

      monitoringRulesSelectedList?.forEach((item) => nextList.push(item));

      monitoringRulesList?.forEach((item) => {
        if (!nextList?.find((listItem) => listItem?.value === item.value)) {
          nextList.push(item);
        }
      });

      setValues('monitoringRuleId', nextList);
    }
  }, [monitoringRulesList, monitoringRulesSelectedList]);

  useEffect(() => {
    if (blacklists) {
      setValues('blacklistId', blacklists);
    }
  }, [blacklists]);

  useEffect(() => {
    if (
      takeCount &&
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !Object.keys(filters).length &&
      !listOfWarnings &&
      !search
    ) {
      getListAction({
        filters: {
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [takeCount, listOfWarnings, filters, search]);

  useEffect(() => {
    if (
      takeCount &&
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      Object.keys(filters).length
    ) {
      const filtersValues: any = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      const nextListFilters: {
        organizationType?: string[];
        transactionTypeId?: string[];
        transactionPaymentType?: string[];
        paymentOrderType?: string[];
      } = {};

      if (filters?.paymentOrderType) {
        nextListFilters.paymentOrderType = filters?.paymentOrderType?.value;
      }

      if (filters?.transactionPaymentType) {
        nextListFilters.transactionPaymentType =
          filters?.transactionPaymentType?.value;
      }

      if (filters?.transactionTypeId) {
        nextListFilters.transactionTypeId = filters?.transactionTypeId?.value;
      }

      if (filters?.organizationType) {
        nextListFilters.organizationType = filters?.organizationType?.value;
      }

      if (Object.keys(nextListFilters)?.length) {
        setFilters(nextListFilters);
      }

      getListAction({
        filters: filtersValues,
      });
    }
  }, [listOfWarnings, takeCount, filters]);

  useEffect(() => {
    if (autocompleteList?.length && filters && filters.organizationId) {
      const unnamedOrganizations: string[] = [];

      filters.organizationId?.value?.forEach((item) => {
        if (!autocompleteList?.find((listItem) => listItem.value === item)) {
          unnamedOrganizations.push(item);
        }
      });

      if (!selectedList?.length) {
        const nextList = filters.organizationId?.value?.filter((item) =>
          unnamedOrganizations?.every((unnamed) => item !== unnamed),
        );

        setSelectedList(
          nextList?.map((item) => ({
            name: autocompleteList?.find((listItem) => listItem.value === item)
              ?.name as string,
            value: item,
          })),
        );

        if (unnamedOrganizations?.length) {
          getOrganizationsByIdsAction(
            unnamedOrganizations,
            nextList?.map((item) => ({
              name: autocompleteList?.find(
                (listItem) => listItem.value === item,
              )?.name as string,
              value: item,
            })),
          );
        }
      }
    }
  }, [autocompleteList, filters]);

  useEffect(() => {
    if (monitoringRulesList?.length && filters && filters.monitoringRuleId) {
      const unnamedRules: string[] = [];

      filters.monitoringRuleId?.value?.forEach((item) => {
        if (!monitoringRulesList?.find((listItem) => listItem.value === item)) {
          unnamedRules.push(item);
        }
      });

      if (!monitoringRulesSelectedList?.length) {
        const nextList = filters.monitoringRuleId?.value?.filter((item) =>
          unnamedRules?.every((unnamed) => item !== unnamed),
        );

        setMonitoringRulesSelectedList(
          nextList?.map((item) => ({
            name: autocompleteList?.find((listItem) => listItem.value === item)
              ?.name as string,
            value: item,
          })),
        );

        if (unnamedRules?.length) {
          getMonitoringRulesByIdsAction(
            unnamedRules,
            nextList?.map((item) => ({
              name: monitoringRulesList?.find(
                (listItem) => listItem.value === item,
              )?.name as string,
              value: item,
            })),
          );
        }
      }
    }
  }, [monitoringRulesList, filters]);

  useEffect(
    () => () => {
      dispatch(clearListOfWarningsState());
    },
    [],
  );

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'warnings-list-report',
        }),
      );
    }
  }, [exportIsSuccess]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.listOfWarningsLoader}>
        <IconLoader />
      </div>
    );
  } else if (!isLoading) {
    content = (
      <>
        <div className={cn.warningsReportOverviewControlsWrapper}>
          <div className={cn.warningsReportOverviewControls}>
            <WarningsReportFilters
              filters={listFilters}
              onFilter={onFilterAction}
              onSearch={onSearch}
              transactionTypes={transactionTypes}
              paymentTypes={paymentTypes}
              listFilters
            />
          </div>
          {activePermissions?.includes(
            PermissionEnum.WARNINGS_DOWNLOAD_ACTION,
          ) && (
            <ButtonSecondary
              text={exportIsLoading ? 'Loading...' : 'Download Report'}
              icon={<IconDownload />}
              className="button-white"
              size={ButtonSize.SMALL}
              onClick={onExportClick}
            />
          )}
        </div>

        <Table
          columns={columns}
          data={formattedData || []}
          sort={{}}
          onSort={() => {}}
          filters={filters}
          error={errorsList || errorList}
          columnsLabels={columnsLabels}
          tableFormClassName={cn.customersListForm}
          onFilterChange={(values) => {
            const filterValues: Filters = {
              ...values,
              take: {
                value: [takeCount?.toString()],
                type: SchemaFilterType.STRING,
              },
            };

            if (values?.organizationId) {
              const nextList: SelectItemProps[] = [];

              selectedList?.forEach((item) => nextList.push(item));

              values?.organizationId?.value?.forEach((item) => {
                if (!nextList.find((listItem) => listItem.value === item)) {
                  nextList.push({
                    name: autocompleteList?.find(
                      (listItem) => listItem.value === item,
                    )?.name as string,
                    value: item,
                  });
                }
              });

              setSelectedList(nextList);
            } else {
              setSelectedList([]);
            }

            onFilter(filterValues);
          }}
          filtersComponent={filtersComponent}
          handleSubmit={handleSubmit}
          isLoading={isFetching}
          take={takeCount?.toString()}
          setPage={(data) => {
            const filterValues: Filters = {
              ...filters,
              take: {
                value: [takeCount?.toString()],
                type: SchemaFilterType.STRING,
              },
            };

            setPage({ ...data, filters: filterValues });
          }}
          pagination={listOfWarningsPagination}
          withSettings
          clickable
          withVerticalScroll
          withScroll
        />
      </>
    );
  }

  return (
    <div className={cn.listOfWarnings} ref={scrollRef}>
      {content}
      <Outlet />
    </div>
  );
};

export default ListOfWarnings;
